import { Badge, Button, Toggle } from "@design";
import { useTranslation } from "react-i18next";
import type { AvailableIntegration, ExtensionConsent, MyConnectorConnection } from "@/api";
import { SettingsList, SettingsRow } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { useConnectorSignIn } from "@/features/build/connectors/useConnectorSignIn";
import { useConsentActions } from "./useConsentActions";

/** One service (integration) with whatever it needs from me: signing in, connecting an account, or a switch. */
export interface Service {
  slug: string;
  name: string;
  description?: string | null;
  consent?: ExtensionConsent;
  /** Its personal sign-in (Slack): reads it as me. */
  personal?: MyConnectorConnection;
  /** A per-person account to connect (Asana, Jira). */
  account?: AvailableIntegration;
}

export interface AccountActions {
  busySlug: string | null;
  connect: (slug: string) => void;
  disconnect: (slug: string) => void;
  signOut: (connection: MyConnectorConnection, slug: string) => void;
}

function StatusBadge({ service }: Readonly<{ service: Service }>) {
  const { t } = useTranslation("settings");
  const { personal, account } = service;
  if (personal?.disabledByAdmin) return <Badge variant="danger">{t("connections.connectors.disabled")}</Badge>;
  if (personal?.connected || account?.connected) return <Badge variant="success">{t("connections.connected")}</Badge>;
  // Switch-only services show their state on the switch; no badge.
  return null;
}

function describe(service: Service, t: (key: string, options?: object) => string): string | undefined {
  const { personal, account } = service;
  if (personal?.connected)
    return personal.account ? t("connections.services.signedInAs", { account: personal.account }) : undefined;
  if (account?.connected) return account.accountLabel ?? service.description ?? undefined;
  if (personal) return personal.description ?? t("connections.services.signInHint", { name: service.name });
  if (account) return t("connections.connectHint", { name: service.name });
  return service.description ?? undefined;
}

function MainControl({ service, accounts }: Readonly<{ service: Service; accounts: AccountActions }>) {
  const { t } = useTranslation("settings");
  const { signIn, pendingUuid } = useConnectorSignIn();
  const consentActions = useConsentActions();
  const { personal, account, consent, name, slug } = service;
  if (personal) {
    if (personal.connected) {
      return (
        <Button
          variant="secondary"
          size="sm"
          loading={accounts.busySlug === `signout:${slug}`}
          onClick={() => accounts.signOut(personal, slug)}
        >
          {t("connections.services.signOut")}
        </Button>
      );
    }
    return (
      <Button
        variant="primary"
        size="sm"
        disabled={personal.disabledByAdmin}
        loading={pendingUuid === personal.uuid}
        onClick={() => signIn(personal.uuid, "/settings/connections")}
      >
        {t("connections.services.signIn", { name })}
      </Button>
    );
  }
  if (account) {
    if (account.connected) {
      return (
        <Button
          variant="secondary"
          size="sm"
          loading={accounts.busySlug === slug}
          onClick={() => accounts.disconnect(slug)}
        >
          {t("connections.disconnect")}
        </Button>
      );
    }
    return (
      <Button
        variant="primary"
        size="sm"
        loading={accounts.busySlug === slug}
        disabled={account.credentialType !== "oauth"}
        onClick={() => accounts.connect(slug)}
      >
        {t("connections.connect")}
      </Button>
    );
  }
  if (!consent?.read.present) return null;
  if (consent.read.consentMode === NOT_ALLOWED) return <NotAllowed />;
  return (
    <Toggle
      aria-label={t("connections.services.turnOn", { name })}
      checked={consent.read.effectiveEnabled}
      disabled={consentActions.isBusy(slug, "read")}
      onChange={(enabled) => void consentActions.toggle(slug, "read", enabled)}
    />
  );
}

// The organization's admin turned it off for everyone: not mine to turn on.
const NOT_ALLOWED = "not_allowed";

function NotAllowed() {
  const { t } = useTranslation("settings");
  return (
    <Text as="span" variant="caption" tone="tertiary">
      {t("connections.services.notAllowed")}
    </Text>
  );
}

/** "Let it take actions": a second switch, shown once the service is on. */
function ActionsSwitch({ service }: Readonly<{ service: Service }>) {
  const { t } = useTranslation("settings");
  const consentActions = useConsentActions();
  const write = service.consent?.write;
  const on = service.personal?.connected || service.account?.connected || service.consent?.read.effectiveEnabled;
  if (!write?.present || !on) return null;
  return (
    <div className="flex items-center justify-between gap-4 pt-2">
      <Text as="span" variant="caption" tone="secondary">
        {t("connections.services.actions")}
      </Text>
      {write.consentMode === NOT_ALLOWED ? (
        <NotAllowed />
      ) : (
        <Toggle
          aria-label={t("connections.services.actionsLabel", { name: service.name })}
          checked={write.effectiveEnabled}
          disabled={consentActions.isBusy(service.slug, "write")}
          onChange={(enabled) => void consentActions.toggle(service.slug, "write", enabled)}
        />
      )}
    </div>
  );
}

/**
 * The services I can turn on (phase 7): one row each, in plain words. Ones that act as me
 * have a Sign in button (signing in turns them on); the rest have a switch.
 */
export function ServicesList({ services, accounts }: Readonly<{ services: Service[]; accounts: AccountActions }>) {
  const { t } = useTranslation("settings");
  const tt = t as unknown as (key: string, options?: object) => string;
  return (
    <SettingsList>
      {services.map((service) => (
        <SettingsRow
          key={service.slug}
          label={service.name}
          description={describe(service, tt)}
          badge={<StatusBadge service={service} />}
        >
          <div className="flex flex-col md:items-end">
            <div className="flex gap-2 md:justify-end">
              <MainControl service={service} accounts={accounts} />
            </div>
            <ActionsSwitch service={service} />
          </div>
        </SettingsRow>
      ))}
    </SettingsList>
  );
}
